import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Home, ArrowLeft, Phone, Search, BarChart3, DollarSign, BookOpen, HelpCircle, Wrench, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import FloatingShapes from "@/components/effects/FloatingShapes";
import AnimatedSection from "@/components/ui/animated-section";
import { GradientText } from "@/components/ui/gradient-text";

const NotFound = () => {
  const location = useLocation();
  const isValentineSeoPath = location.pathname.startsWith('/valentine-seo');

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  // Valentine SEO specific links
  const valentineSeoLinks = [
    { label: "Platform", path: "/valentine-seo", icon: BarChart3 },
    { label: "Pricing", path: "/valentine-seo/pricing", icon: DollarSign },
    { label: "Case Studies", path: "/valentine-seo/case-studies", icon: BookOpen },
    { label: "Tools", path: "/valentine-seo/tools", icon: Wrench },
    { label: "Help Center", path: "/valentine-seo/help", icon: HelpCircle },
  ];

  // Main website links
  const mainSiteLinks = [
    { label: "Home", path: "/", icon: Home },
    { label: "Services", path: "/services", icon: Search },
    { label: "Portfolio", path: "/portfolio", icon: ArrowLeft },
    { label: "Contact Us", path: "/contact", icon: Phone },
  ];

  const suggestedLinks = isValentineSeoPath ? valentineSeoLinks : mainSiteLinks;

  return (
    <div className="min-h-screen bg-primary relative overflow-hidden flex items-center justify-center px-4">
      {/* Premium background effects */}
      <FloatingShapes variant="hero" />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
      
      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary-foreground)) 1px, transparent 1px),
                           linear-gradient(90deg, hsl(var(--primary-foreground)) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />
      
      {/* Accent glow orbs */}
      <div className="absolute top-1/4 left-10 w-64 h-64 bg-accent/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-accent/15 rounded-full blur-3xl" />

      <div className="max-w-lg w-full text-center relative z-10">
        <AnimatedSection animation="fade-up">
          {/* Valentine SEO Branding */}
          {isValentineSeoPath && (
            <div className="mb-6">
              <span className="text-2xl font-bold text-primary-foreground">
                Valentine<span className="text-accent">SEO</span>
              </span>
            </div>
          )}

          {/* Premium badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 backdrop-blur-sm rounded-full mb-8 border border-accent/30">
            <Sparkles className="h-4 w-4 text-accent" />
            <span className="text-sm font-semibold text-accent">Page Not Found</span>
          </div>

          {/* 404 Number with glow effect */}
          <div className="relative mb-8">
            <h1 className="text-[150px] md:text-[200px] font-black text-primary-foreground/5 leading-none select-none">
              404
            </h1>
            <div className="absolute inset-0 flex items-center justify-center">
              <GradientText variant="accent" className="text-6xl md:text-8xl font-bold drop-shadow-[0_0_40px_hsl(var(--accent)/0.5)]">
                Oops!
              </GradientText>
            </div>
          </div>

          {/* Message */}
          <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
            This Page Wandered Off
          </h2>
          <p className="text-primary-foreground/70 mb-8 max-w-md mx-auto">
            {isValentineSeoPath 
              ? "The page you're looking for doesn't exist or has been moved. Let's get you back to the platform."
              : "The page you're looking for doesn't exist or has been moved. Let's get you back on track."
            }
          </p>

          {/* CTA Buttons with premium styling */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              size="lg"
              variant="glow"
              className="group min-w-[180px]"
              asChild
            >
              <Link to={isValentineSeoPath ? "/valentine-seo" : "/"}>
                {isValentineSeoPath ? <BarChart3 className="mr-2 h-4 w-4" /> : <Home className="mr-2 h-4 w-4" />}
                {isValentineSeoPath ? "Back to Platform" : "Back to Home"}
              </Link>
            </Button>
            <Button
              size="lg"
              variant="glass"
              className="border-primary-foreground/30 text-primary-foreground min-w-[180px]"
              asChild
            >
              <Link to={isValentineSeoPath ? "/valentine-seo/help" : "/contact"}>
                {isValentineSeoPath ? <HelpCircle className="mr-2 h-4 w-4" /> : <Phone className="mr-2 h-4 w-4" />}
                {isValentineSeoPath ? "Get Help" : "Contact Support"}
              </Link>
            </Button>
          </div>

          {/* Helpful Links with premium glass cards */}
          <div className="border-t border-primary-foreground/10 pt-8">
            <p className="text-sm text-primary-foreground/50 mb-4 uppercase tracking-wider font-medium">
              Popular pages you might be looking for
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {suggestedLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-foreground/5 backdrop-blur-sm border border-primary-foreground/10 text-primary-foreground text-sm hover:bg-primary-foreground/10 hover:border-accent/30 hover:text-accent transition-all duration-300"
                >
                  <link.icon className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
      
      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background/50 to-transparent" />
    </div>
  );
};

export default NotFound;
